import { useCallback, useEffect, useState } from 'react';
import { Typography, IconButton, Badge, Avatar, Menu, MenuItem, Box, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportIcon from '@mui/icons-material/Support';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import AppInstallPrompt from './AppInstallPrompt';
import api from '../api/api';

import { languages } from '../locales/languages';

export default function TopBar({ isMobile, handleDrawerToggle }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const open = Boolean(anchorEl);
  const profileOpen = Boolean(profileAnchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const navigate = useNavigate();
  const { clearData, user } = useApp();
  const { t, currentLanguage, changeLanguage, getCurrentLanguage } = useLanguage();

  const fetchNotifications = useCallback(async () => {
    if (!sessionStorage.getItem('accessToken')) {
      setNotifications([]);
      return;
    }

    try {
      setNotificationLoading(true);
      const response = await api.get('/notifications');
      const items = Array.isArray(response.data?.notifications)
        ? response.data.notifications
        : [];
      setNotifications(items);
    } catch (error) {
      if (error.response?.status === 401) {
        setNotifications([]);
      }
    } finally {
      setNotificationLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 60000);

    return () => window.clearInterval(intervalId);
  }, [fetchNotifications]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setAnchorEl(null);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleClearAllNotifications = async () => {
    try {
      await api.post('/notifications/read');
      setNotifications([]);
    } catch {
      fetchNotifications();
    } finally {
      setNotificationAnchorEl(null);
    }
  };

  const handleNotificationOpen = async (notification) => {
    setNotifications((current) => current.filter((item) => item.id !== notification.id));
    setNotificationAnchorEl(null);

    try {
      await api.post('/notifications/read', { notification_id: notification.id });
    } catch {
      fetchNotifications();
    }

    if (notification.path) {
      navigate(notification.path);
      return;
    }

    if (notification.type === 'ticket_reply') {
      navigate('/dashboard/support-tickets');
    } else if (notification.type === 'security_login') {
      navigate('/dashboard/account-settings?tab=security');
    }
  };

  const handleLogout = () => {
    clearData();
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('client_id');
    navigate('/');
    handleProfileClose();
  };

  const handleWallets = () => {
    navigate('/dashboard/wallets');
    handleProfileClose();
  };

  const handleSettings = () => {
    navigate('/dashboard/account-settings');
    handleProfileClose();
  };

  const handleSupportTickets = () => {
    navigate('/dashboard/support-tickets');
    handleProfileClose();
  };

  const formatNotificationDate = (value) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const userEmail = user?.email?.trim() || 'user@example.com';
  const userInitial = userEmail.charAt(0).toUpperCase();
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const unreadCount = notifications.length;

  return (
    <div className="topbar-container">
      <div className="topbar-left">
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className="hamburger-button"
          >
            <MenuIcon />
          </IconButton>
        )}
      </div>

      <div className="topbar-actions">
        <AppInstallPrompt />

        <IconButton className="topbar-icon-button" onClick={handleClick}>
          <img src={getCurrentLanguage().flag} alt="Language" className="language-flag" />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          classes={{ paper: 'language-menu-paper' }}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className="language-menu-item"
              selected={lang.code === currentLanguage}
            >
              <img src={lang.flag} alt={lang.name} className="menu-flag-icon" />
              {lang.name}
            </MenuItem>
          ))}
        </Menu>

        <IconButton className="topbar-icon-button" onClick={handleNotificationClick}>
          <Badge badgeContent={unreadCount} className="notification-badge">
            <NotificationsIcon />
          </Badge>
        </IconButton>

        <IconButton className="topbar-icon-button" onClick={handleProfileClick}>
          <Avatar className="user-avatar">{userInitial}</Avatar>
        </IconButton>

        <Menu
          anchorEl={profileAnchorEl}
          open={profileOpen}
          onClose={handleProfileClose}
          classes={{ paper: 'profile-menu-paper' }}
          PaperProps={{
            sx: {
              backgroundColor: 'rgb(31, 41, 55)',
              color: 'white',
              borderRadius: '8px',
              minWidth: '200px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
              {userEmail}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {currentDate}
            </Typography>
          </Box>

          <MenuItem onClick={handleWallets} sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            py: 1.5
          }}>
            <AccountBalanceWalletIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('wallets')}
          </MenuItem>

          <MenuItem onClick={handleSettings} sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            py: 1.5
          }}>
            <SettingsIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('accountSettings')}
          </MenuItem>

          <MenuItem onClick={handleSupportTickets} sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            py: 1.5
          }}>
            <SupportIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('supportTickets')}
          </MenuItem>

          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />

          <MenuItem onClick={handleLogout} sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            py: 1.5,
            justifyContent: 'center'
          }}>
            <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('logout')}
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={notificationAnchorEl}
          open={notificationOpen}
          onClose={handleNotificationClose}
          classes={{ paper: 'notification-menu-paper' }}
          PaperProps={{
            sx: {
              backgroundColor: 'rgb(17, 25, 39)',
              color: 'white',
              borderRadius: '8px',
              minWidth: '320px',
              maxWidth: '400px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
              {t('notification')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('youHave')} {unreadCount} {t('unreadNotifications')}
            </Typography>
          </Box>

          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {notificationLoading ? t('loading') : t('noNotifications')}
                </Typography>
              </Box>
            ) : (
              notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationOpen(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 2,
                    whiteSpace: 'normal',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: notification.type === 'security_login'
                        ? 'rgba(245, 158, 11, 0.2)'
                        : 'rgba(59, 130, 246, 0.2)',
                      color: notification.type === 'security_login' ? '#f59e0b' : '#60a5fa',
                      border: notification.type === 'security_login'
                        ? '1px solid rgba(245, 158, 11, 0.35)'
                        : '1px solid rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    {notification.type === 'security_login'
                      ? <SecurityIcon fontSize="small" />
                      : <SupportIcon fontSize="small" />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                      {notification.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.75)', mb: 0.5 }}>
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {formatNotificationDate(notification.date)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Box>

          {notifications.length > 0 && (
            <>
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#3b82f6',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onClick={handleClearAllNotifications}
                >
                  {t('clearAll')}
                </Typography>
              </Box>
            </>
          )}
        </Menu>
      </div>
    </div>
  );
}
